/**
 * Embedded Signup abandonment / error telemetry (INT-1216).
 *
 * Meta posts abandonment (`current_step`) and error-report (`error_code`) data
 * to the browser during the Embedded Signup popup. Until INT-1216 we received
 * those messages and threw them away, leaving 93% of connection failures
 * (`popup_closed`) with no diagnostic detail at all.
 *
 * WHY THIS IS A MODULE SINGLETON AND NOT PART OF THE HOOK
 * ------------------------------------------------------
 * `useEmbeddedSignupSession` is mounted more than once at the same time:
 * `Dashboard.component.tsx` mounts it for the dashboard's own FB.login flow,
 * and `WhatsAppBusinessConnectionModal` mounts it again (through
 * `useWhatsAppConnectState`) whenever the connection modal is open — which is
 * the primary connection path. That is harmless today because each instance
 * only writes its own ref. If emission lived in the hook, every `CANCEL` would
 * be recorded two or three times and the funnel would be wrong from day one.
 *
 * So: exactly one `window` listener for the whole application, installed
 * idempotently, plus content-keyed dedupe as a second line of defence against
 * Meta re-posting the same message.
 *
 * ATTEMPT CORRELATION
 * -------------------
 * Meta's `session_id` arrives ONLY on error reports. Abandonments carry nothing
 * but the step, and abandonment is the 93% case — so an id we issue ourselves
 * at popup launch is the only way to tie an abandonment to a workspace, user
 * and attempt. `beginEmbeddedSignupAttempt` issues it; every terminal outcome
 * calls `finalizeEmbeddedSignupAttempt`.
 *
 * Known limitation: Meta's messages carry no reference to our attempt id, so a
 * message from a previous popup that arrives while a *new* attempt is open is
 * indistinguishable from one belonging to the new attempt and will be
 * attributed to it. Messages arriving after an attempt is finalized are dropped
 * instead, which covers the realistic ordering.
 */

import {
  parseEmbeddedSignupMessage,
  type tEmbeddedSignupMessage,
} from 'src/lib/facebook/embeddedSignup';
import type { tEmbeddedSignupPath } from 'src/lib/facebook/embeddedSignupLogin';

/**
 * Where the popup was launched from. Recorded on the attempt rather than
 * derived from the route: `deriveWapiContext` in `useWapiAnalytics` maps the
 * onboarding path to 'chat', which would mislabel the funnel, and changing that
 * function would alter existing events. `onboarding` is the onboarding connect
 * screen; `onboarding_wizard` is the connection wizard opened from onboarding.
 */
export type tEmbeddedSignupSurface =
  'onboarding' | 'onboarding_wizard' | 'dashboard_modal' | 'dashboard';

export type tEmbeddedSignupRecord = {
  /** Our own attempt id; `null` when the event could not be correlated. */
  attemptId: string | null;
  /** Launch surface of the attempt; `null` when the caller declared none. */
  surface: tEmbeddedSignupSurface | null;
  /** Embedded Signup flow the attempt opened; `null` when the caller declared none. */
  path: tEmbeddedSignupPath | null;
  /** Raw Meta `event` string, never mapped onto an internal enum. */
  event: string;
  /** Raw Meta envelope `version`. */
  version: string;
  kind: 'abandoned' | 'error_report';
  /** Raw step string for abandonments; `null` on error reports. */
  currentStep: string | null;
  errorCode: number | null;
  errorMessage: string | null;
  /** Meta's own `session_id` — error reports only. */
  metaSessionId: string | null;
  metaTimestamp: string | null;
  /** True when no attempt was open, so the record cannot be correlated. */
  orphaned: boolean;
};

export type tEmbeddedSignupSink = (record: tEmbeddedSignupRecord) => void;

type tRegisteredSink = {
  sink: tEmbeddedSignupSink;
  /**
   * Optional identity. Only the NEWEST registration under a key receives
   * records, so a hook mounted on several surfaces at once cannot emit the same
   * record twice. Superseded entries stay in the list and become active again
   * when the newer one unregisters. Anonymous sinks (no key) always stack.
   */
  key: string | null;
};

// Upper bound on remembered dedupe keys. A single connection attempt produces a
// handful of messages, so this is generous; the cap only exists so a long-lived
// session cannot grow the set without limit.
const MAX_DEDUPE_KEYS = 200;

let listenerInstalled = false;
let currentAttemptId: string | null = null;
let currentSurface: tEmbeddedSignupSurface | null = null;
let currentPath: tEmbeddedSignupPath | null = null;
let attemptFinalized = false;
let emittedKeys: string[] = [];
let sinks: tRegisteredSink[] = [];

function newAttemptId(): string {
  const webCrypto = globalThis.crypto;
  if (webCrypto && typeof webCrypto.randomUUID === 'function') {
    return webCrypto.randomUUID();
  }
  // Fallback for browsers without randomUUID: uniqueness only has to hold
  // within one workspace's event stream, not globally.
  return `esu-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

function dedupeKeyFor(record: tEmbeddedSignupRecord): string {
  return [
    record.attemptId ?? 'no-attempt',
    record.event,
    record.kind,
    record.currentStep ?? '',
    record.errorCode ?? '',
    record.metaSessionId ?? '',
  ].join('|');
}

function alreadyEmitted(key: string): boolean {
  if (emittedKeys.includes(key)) return true;
  emittedKeys.push(key);
  if (emittedKeys.length > MAX_DEDUPE_KEYS) {
    // Keep the most recent half: trimming the oldest keys preserves dedupe for
    // anything the popup is plausibly still re-posting.
    emittedKeys = emittedKeys.slice(-Math.floor(MAX_DEDUPE_KEYS / 2));
  }
  return false;
}

/**
 * The sinks that should receive a record: every anonymous sink, plus the NEWEST
 * registration for each key.
 *
 * Resolving this at dispatch time rather than evicting on registration is what
 * makes the lifecycle safe. The Dashboard route mounts the telemetry hook, then
 * the connection modal mounts it again and supersedes it; when the modal closes,
 * the Dashboard is still mounted and its own FB.login must keep emitting. If
 * registration evicted the previous entry, closing the modal would leave the key
 * with no sink at all and the Dashboard would go silently dark.
 */
function activeSinks(): tRegisteredSink[] {
  const newestIndexByKey = new Map<string, number>();
  sinks.forEach((registered, index) => {
    if (registered.key !== null) newestIndexByKey.set(registered.key, index);
  });

  // Registration order is preserved so sink behavior stays predictable.
  return sinks.filter(
    (registered, index) =>
      registered.key === null || newestIndexByKey.get(registered.key) === index,
  );
}

function toRecord(parsed: tEmbeddedSignupMessage): tEmbeddedSignupRecord | null {
  const base = {
    attemptId: currentAttemptId,
    surface: currentSurface,
    path: currentPath,
    event: parsed.event,
    version: parsed.version,
    orphaned: currentAttemptId === null,
  };

  if (parsed.kind === 'abandoned') {
    return {
      ...base,
      kind: 'abandoned',
      currentStep: parsed.currentStep,
      errorCode: null,
      errorMessage: null,
      metaSessionId: null,
      metaTimestamp: null,
    };
  }

  if (parsed.kind === 'error_report') {
    return {
      ...base,
      kind: 'error_report',
      currentStep: null,
      errorCode: parsed.errorCode,
      errorMessage: parsed.errorMessage || null,
      metaSessionId: parsed.metaSessionId || null,
      metaTimestamp: parsed.metaTimestamp || null,
    };
  }

  // `session` payloads belong to the connection path, not to telemetry.
  return null;
}

function handleMessage(event: MessageEvent) {
  const parsed = parseEmbeddedSignupMessage(event);
  if (!parsed) return;

  // A completed onboarding closes the attempt, so a popup dismissed after a
  // successful FINISH is never recorded as an abandonment.
  if (parsed.kind === 'session') {
    attemptFinalized = true;
    return;
  }

  if (attemptFinalized) return;

  const record = toRecord(parsed);
  if (!record) return;
  if (alreadyEmitted(dedupeKeyFor(record))) return;

  // Each sink is isolated: a failing PostHog call must not stop the HTTP write,
  // and neither may throw into Meta's message dispatch.
  activeSinks().forEach((registered) => {
    try {
      registered.sink(record);
    } catch {
      // Telemetry is strictly best-effort — swallow and continue.
    }
  });
}

/**
 * Installs the single `window` message listener. Safe to call from every
 * surface that opens the popup; subsequent calls are no-ops.
 */
export function installEmbeddedSignupTelemetry(): void {
  if (listenerInstalled) return;
  listenerInstalled = true;
  window.addEventListener('message', handleMessage);
}

/**
 * Registers a sink and returns its unregister function.
 *
 * Pass a `key` for sinks that may be mounted from more than one surface at the
 * same time (the PostHog and HTTP sinks both are). Only the newest registration
 * under a key receives records, so a record is delivered exactly once — but
 * superseded entries are KEPT, and become active again when the newer one
 * unregisters. That is what lets the Dashboard keep emitting after the
 * connection modal closes. See `activeSinks`.
 *
 * Unregistering removes only this caller's own entry (by identity), so a
 * component unmounting after being superseded cannot tear down its replacement.
 */
export function registerEmbeddedSignupSink(
  sink: tEmbeddedSignupSink,
  key?: string,
): () => void {
  const entry: tRegisteredSink = { sink, key: key ?? null };
  sinks.push(entry);
  return () => {
    sinks = sinks.filter((registered) => registered !== entry);
  };
}

/**
 * Opens a new connection attempt and returns its id. Call this where the popup
 * is launched, next to the existing "clear the previous sessionInfo" reset.
 *
 * `surface` and `path` are optional so callers that do not declare them produce
 * `null` values rather than being silently mislabelled with a default.
 *
 * @param {tEmbeddedSignupSurface} [surface] - Where the popup was launched from.
 * @param {tEmbeddedSignupPath} [path] - The Embedded Signup flow the popup opens.
 * @returns {string} The id of the new attempt.
 */
export function beginEmbeddedSignupAttempt(
  surface?: tEmbeddedSignupSurface,
  path?: tEmbeddedSignupPath,
): string {
  currentAttemptId = newAttemptId();
  currentSurface = surface ?? null;
  currentPath = path ?? null;
  attemptFinalized = false;
  emittedKeys = [];
  return currentAttemptId;
}

/**
 * Closes the current attempt. Call on every terminal outcome — success,
 * `api_error`, `sdk_error` and `popup_closed`. Idempotent.
 */
export function finalizeEmbeddedSignupAttempt(): void {
  attemptFinalized = true;
}

/** Current attempt id, for attaching to analytics emitted elsewhere. */
export function getEmbeddedSignupAttemptId(): string | null {
  return currentAttemptId;
}

/** Test-only: tears the singleton down so each test starts clean. */
export function __resetEmbeddedSignupTelemetry(): void {
  if (listenerInstalled) window.removeEventListener('message', handleMessage);
  listenerInstalled = false;
  currentAttemptId = null;
  currentSurface = null;
  currentPath = null;
  attemptFinalized = false;
  emittedKeys = [];
  sinks = [];
}
